import axios from "axios";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api/v1";

export const TOKEN_KEY = "businessToken";
export const USER_KEY = "businessUser";
export const REFRESH_KEY = "businessRefreshToken";
export const EXPIRES_KEY = "businessTokenExpiresAt";

// Refresh this long before the access token expires, so no request meets it.
const REFRESH_EARLY_MS = 5 * 60 * 1000;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { Accept: "application/json" },
});

/** Keep a sign-in or refresh response: access token, refresh token, expiry. */
export const storeTokens = (data) => {
  localStorage.setItem(TOKEN_KEY, data.access_token);
  if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  if (data.expires_in) {
    localStorage.setItem(EXPIRES_KEY, String(Date.now() + Number(data.expires_in) * 1000));
  }
};

export const clearTokens = () => {
  [TOKEN_KEY, USER_KEY, REFRESH_KEY, EXPIRES_KEY].forEach((key) => localStorage.removeItem(key));
};

// The endpoints whose 401 is an answer, not an expired session.
const AUTH_PATHS = ["/business/login", "/business/2fa/challenge", "/business/refresh"];
const isAuthCall = (config) => AUTH_PATHS.some((path) => (config?.url ?? "").endsWith(path));

/*
 * One refresh at a time. Every caller that finds the session stale awaits the
 * same promise, so a burst of 401s costs one refresh. Across tabs a Web Lock
 * serialises them, and a tab that finds the stored refresh token already
 * changed by another tab just uses the new access token: spending the same
 * refresh token twice would read as theft and sign every session out.
 */
let refreshing = null;

export const refreshSession = () => {
  if (refreshing) return refreshing;

  const seen = localStorage.getItem(REFRESH_KEY);

  const run = async () => {
    const current = localStorage.getItem(REFRESH_KEY);
    if (!current) throw new Error("No refresh token");
    if (current !== seen) return localStorage.getItem(TOKEN_KEY);

    // Plain axios: this call must not pass through the interceptors below.
    const { data } = await axios.post(
      `${API_BASE_URL}/business/refresh`,
      { refresh_token: current },
      { headers: { Accept: "application/json" } }
    );
    storeTokens(data);
    return data.access_token;
  };

  const locked = navigator.locks?.request
    ? navigator.locks.request("business-token-refresh", run)
    : run();

  refreshing = locked.finally(() => {
    refreshing = null;
  });

  return refreshing;
};

// A burst of failing requests ends the session once.
let ending = false;

const endSession = async () => {
  if (ending) return;
  ending = true;
  clearTokens();
  // No logout call happened, so the server still holds this browser's push
  // token: drop it at FCM before leaving, or the old account's pushes keep
  // arriving here. Imported lazily: push.js reaches this module itself.
  try {
    const { resetPush } = await import("./push");
    await resetPush();
  } catch {
    // Push is optional.
  }
  if (window.location.pathname !== "/login") window.location.href = "/login";
  else ending = false;
};

// Attach the JWT issued by /business/login, and tell the API which language to
// return translatable names (cities, categories, currencies) in. A token about
// to expire is refreshed first.
api.interceptors.request.use(async (config) => {
  const expiresAt = Number(localStorage.getItem(EXPIRES_KEY));
  if (
    !isAuthCall(config) &&
    localStorage.getItem(REFRESH_KEY) &&
    expiresAt &&
    Date.now() > expiresAt - REFRESH_EARLY_MS
  ) {
    try {
      await refreshSession();
    } catch {
      // Let the request go; its 401 takes the path below.
    }
  }

  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;

  config.headers["Accept-Language"] = localStorage.getItem("businessLang") || "en";
  return config;
});

// A 401 gets one refresh and one retry. Only when that fails is the session
// over — clear it and go back to login.
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    if (err.response?.status !== 401 || !original || isAuthCall(original)) {
      return Promise.reject(err);
    }

    if (original._retried || !localStorage.getItem(REFRESH_KEY)) {
      endSession();
      return Promise.reject(err);
    }

    original._retried = true;

    try {
      const token = await refreshSession();
      original.headers.Authorization = `Bearer ${token}`;
      return api(original);
    } catch {
      endSession();
      return Promise.reject(err);
    }
  }
);

/**
 * Pull a readable message out of a Laravel error response.
 * The API answers with {"error": "..."} and validation with {"errors": {...}}.
 */
export const apiErrorMessage = (err, fallback = "Something went wrong.") => {
  const data = err.response?.data;
  if (!data) return err.message || fallback;
  if (data.error) return data.error;
  if (data.errors) return Object.values(data.errors).flat()[0] || fallback;
  return data.message || fallback;
};

/** Field-keyed validation errors, or an empty object. */
export const apiFieldErrors = (err) => err.response?.data?.errors ?? {};

export default api;
